import {
  Card,
  CardContent,
  Typography,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Alert,
  AlertDescription,
} from "@altimateai/lego";
import { FlexListSummary } from "../../components/listSummary";
import DataTable from "../../components/tables/DataTable";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { RelativeComponentLoader } from "../../components/loader";
import { Paginate } from "../../components/paginate";
import { Sheet, SheetContent } from "@altimateai/lego";
import type { TableHeaderType } from "@/components/tables/types";
import { RoleDetailsModal, UserDetailsModal } from "./DetailsModal";
import styles from "./styles.module.scss";
import {
  getGovernanceFilters,
  getGovernanceRoles,
  getGovernanceSummary,
  getGovernanceUsers,
} from "../../helpers/apis";
import { ColorInsightTags, FilterTagWrapper } from "../../components/tags";
import { formatNumber } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import SavedFilters from "@/components/tags/SavedFilters";
import usePaginate from "@/components/paginate/usePaginate";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { DownloadTable } from "@/components/downloadTable";
import { downloadRolesCSV, downloadUsersCSV } from "@/helpers/apis";
import { getInstanceName } from "@/helpers/utils";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import {
  GovernanceFilterEntityTypes,
  type GovernanceUser,
  type GovernanceRole,
  type UsersTableFilterParams,
  type RolesTableFilterParams,
} from "@/pages/governance/types";
import { FilterType, createAPIDropdownFilter } from "@/components/tables/types";
import { parseSortDirection } from "@/components/tables/types";
import { useBreadcrumb } from "@/components/breadcrumbs";

const GET_USERS = "GET-USERS";
const GET_ROLES = "GET-ROLES";
const GET_SUMMARY = "GET-SUMMARY";

interface UsersListProps {
  onItemClick: (user: GovernanceUser) => void;
}

const UsersList = ({ onItemClick }: UsersListProps) => {
  const [searchParams] = useSearchParams();
  const { deleteSearchParamsByKeyValue, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { currency, datastores } = useAppState();
  const { page, pageSize, setPage, updatePageSize } = usePaginate();

  const userFilter = searchParams.getAll("user");
  const roleFilter = searchParams.getAll("role");
  const insightFilter = searchParams.getAll("tag");
  const instanceFilter = searchParams.getAll("instance");
  const sortAttribute = searchParams.get("sort_key") || "total_cost";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"), "desc");

  const getFilterParams = (): UsersTableFilterParams => {
    const params: UsersTableFilterParams = {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: (sortOrder || "desc") as string,
    };
    if (userFilter.length > 0) params.users = userFilter;
    if (roleFilter.length > 0) params.roles = roleFilter;
    if (insightFilter.length > 0) params.insights = insightFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    return params;
  };

  const {
    data: users,
    isFetching: isLoading,
    isError,
  } = useQuery({
    queryKey: [
      GET_USERS,
      sortAttribute,
      sortOrder,
      page,
      pageSize,
      ...userFilter,
      ...roleFilter,
      ...insightFilter,
      ...instanceFilter,
    ],
    queryFn: () => getGovernanceUsers(getFilterParams()),
  });

  const headers: TableHeaderType<GovernanceUser>[] = [
    {
      id: 1,
      label: "Username",
      uid: "user_name",
      width: "15%",
      render: item => <span className={"al-break-all al-text-brand"}>{item.user_name}</span>,
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        apiFunction: ({ prefix, page }) =>
          getGovernanceFilters({
            entity_type: GovernanceFilterEntityTypes.UserNames,
            prefix,
            page,
          }),
        pageSize: 50,
        seeMore: true,
        extractorFunction: data => {
          return (
            data?.users?.user_names?.map(t => ({
              label: t,
              value: t,
            })) || []
          );
        },
        searchKey: "user",
        value: userFilter,
        onChange: value => {
          updateSearchParamsByKey({ user: value });
        },
      }),
    },
    {
      id: 2,
      label: "Total Query Cost",
      uid: "total_cost",
      render: item => <div>{formatNumber(item.total_cost, { currency })}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 3,
      label: "Max Query Cost",
      uid: "max_cost",
      render: item => <div>{formatNumber(item.max_cost, { currency })}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 4,
      label: "Avg Query Cost",
      uid: "avg_cost",
      render: item => <div>{formatNumber(item.avg_cost, { currency })}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 5,
      label: "Queries",
      uid: "num_queries",
      render: item => <div>{item.num_of_queries}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 6,
      label: "Query Groups",
      uid: "num_query_groups",
      render: item => <div>{item.num_of_query_groups}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 7,
      label: "Adhoc Queries",
      uid: "adhoc_queries",
      render: item => <div>{item.num_of_adhoc_queries}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 8,
      label: "Insights",
      uid: "insights",
      width: "15%",
      render: item => <ColorInsightTags tags={item.insights || []} />,
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        apiFunction: ({ prefix, page }) =>
          getGovernanceFilters({
            entity_type: GovernanceFilterEntityTypes.UsersInsights,
            prefix,
            page,
          }),
        pageSize: 50,
        seeMore: true,
        extractorFunction: data => {
          return (
            data?.users?.insights?.map(t => ({
              label: t,
              value: t,
            })) || []
          );
        },
        value: insightFilter,
        searchKey: "tag",
        onChange: value => {
          updateSearchParamsByKey({ tag: value });
        },
      }),
    },
    {
      id: 9,
      label: "Instance",
      uid: "instance_id",
      width: "15%",
      render: item => (
        <div>{getInstanceName(item.instance_id, datastores ?? []) || item.instance_id}</div>
      ),
      filter: {
        filterType: FilterType.DROPDOWN,
        value: instanceFilter,
        searchKey: "instance",
        options: (datastores ?? [])
          .filter(instance => instance.instance_id != null)
          .map(instance => ({
            label: instance.name,
            value: instance.instance_id as string,
          })),
        onChange: value => {
          updateSearchParamsByKey({ instance: value });
        },
      },
    },
    {
      id: 10,
      label: "Default Role",
      uid: "default_role",
      width: "7%",
      render: item => <div className={styles.table_item_break_word}>{item.default_role}</div>,
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        apiFunction: ({ prefix, page }) =>
          getGovernanceFilters({
            entity_type: GovernanceFilterEntityTypes.UsersDefaultRoles,
            prefix,
            page,
          }),
        pageSize: 50,
        seeMore: true,
        extractorFunction: data => {
          return (
            data?.users?.default_roles?.map(t => ({
              label: t,
              value: t,
            })) || []
          );
        },
        value: roleFilter,
        searchKey: "role",
        onChange: value => {
          updateSearchParamsByKey({ role: value });
        },
      }),
    },
  ];

  const handleDownloadClick = async (): Promise<void> => {
    const params = getFilterParams();
    await downloadUsersCSV(params);
  };

  if (isLoading) return <RelativeComponentLoader label={"Loading..."} />;

  if (isError || !users) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load users data. Please try again later.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent className="al-pt-4">
        <Stack direction="row" gap="sm" align="center" className="mb-4">
          <div className="text-black">Users</div>
          <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.USERS} />
          <FilterTagWrapper
            applicableFor={FilterApplicableFor.GOVERNANCE_USERS}
            filters={{
              userFilter: {
                filterStr: userFilter,
                label: "User",
                searchKey: "user",
                onclose: (removedFilter?: string) => {
                  if (removedFilter) deleteSearchParamsByKeyValue({ user: removedFilter });
                },
              },
              roleFilter: {
                filterStr: roleFilter,
                label: "Role",
                searchKey: "role",
                onclose: (removedFilter?: string) => {
                  if (removedFilter) deleteSearchParamsByKeyValue({ role: removedFilter });
                },
              },
              insightFilter: {
                filterStr: insightFilter,
                label: "Tag",
                searchKey: "tag",
                onclose: (removedFilter?: string) => {
                  if (removedFilter) deleteSearchParamsByKeyValue({ tag: removedFilter });
                },
              },
              instanceFilter: {
                filterStr: instanceFilter
                  .map(instance => getInstanceName(instance, datastores ?? []))
                  .filter((x): x is string => x !== undefined),
                label: "Instance",
                searchKey: "instance",
                onclose: (removedFilter?: string) => {
                  const instanceId = (datastores ?? []).find(
                    ds => ds.name === removedFilter
                  )?.instance_id;
                  if (instanceId) {
                    deleteSearchParamsByKeyValue({ instance: instanceId });
                  }
                },
              },
            }}
          />
          <div className="spacer" />
          <div className={styles.duration}>Last 1 Month</div>
          <SavedFilters
            applicableFor={FilterApplicableFor.GOVERNANCE_USERS}
            buttonProps={{ size: "sm" }}
          />
          <div id="columnpicker-container-users" className={styles.columnpicker} />
          <DownloadTable
            handleDownload={handleDownloadClick}
            buttonProps={{
              size: "sm",
            }}
          />
        </Stack>
        <DataTable
          header={headers}
          data={users}
          idKey="user_name"
          onRowClick={onItemClick}
          resetPage={() => setPage(0)}
          applicableFor={FilterApplicableFor.GOVERNANCE_USERS}
          columnPickerPortal="columnpicker-container-users"
          minWidth={1100}
          defaultSort={{ sortParamValue: "total_cost", order: "desc" }}
        />
        <Paginate
          itemCount={users.total}
          page={page}
          pageSize={pageSize}
          numPages={users.pages}
          onPageClick={(v: number) => setPage(v)}
          onPageSizeChange={(v: number) => updatePageSize(v)}
          showPageSize={true}
        />
      </CardContent>
    </Card>
  );
};

interface RolesListProps {
  onItemClick: (role: GovernanceRole) => void;
}

const RolesList = ({ onItemClick }: RolesListProps) => {
  const [searchParams] = useSearchParams();
  const { deleteSearchParamsByKeyValue, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { currency, datastores } = useAppState();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const roleFilter = searchParams.getAll("role");
  const insightFilter = searchParams.getAll("tag");
  const instanceFilter = searchParams.getAll("instance");
  const sortAttribute = searchParams.get("sort_key") || "total_cost";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"), "desc");

  const getFilterParams = (): RolesTableFilterParams => {
    const params: RolesTableFilterParams = {
      page: page + 1,
      size: pageSize,
      sort_key: sortAttribute,
      sort_order: (sortOrder || "desc") as string,
    };
    if (roleFilter.length > 0) params.roles = roleFilter;
    if (insightFilter.length > 0) params.insights = insightFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    return params;
  };

  const {
    data: roles,
    isFetching: isLoading,
    isError,
  } = useQuery({
    queryKey: [
      GET_ROLES,
      sortAttribute,
      sortOrder,
      page,
      pageSize,
      ...roleFilter,
      ...insightFilter,
      ...instanceFilter,
    ],
    queryFn: () => getGovernanceRoles(getFilterParams()),
  });

  const handleDownloadClick = async (): Promise<void> => {
    const params = getFilterParams();
    await downloadRolesCSV(params);
  };

  const headers: TableHeaderType<GovernanceRole>[] = [
    {
      id: 1,
      label: "Role Name",
      uid: "role_name",
      width: "15%",
      render: item => <span className={"al-break-all al-text-brand"}>{item.role_name}</span>,
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        apiFunction: ({ prefix, page }) =>
          getGovernanceFilters({
            entity_type: GovernanceFilterEntityTypes.RoleNames,
            prefix,
            page,
          }),
        pageSize: 50,
        seeMore: true,
        extractorFunction: data => {
          return (
            data?.roles?.role_names?.map(t => ({
              label: t,
              value: t,
            })) || []
          );
        },
        value: roleFilter,
        searchKey: "role",
        onChange: value => {
          updateSearchParamsByKey({ role: value });
        },
      }),
    },
    {
      id: 2,
      label: "Total Query Cost",
      uid: "total_cost",
      render: item => <div>{formatNumber(item.total_cost, { currency })}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 3,
      label: "Max Query Cost",
      uid: "max_cost",
      render: item => <div>{formatNumber(item.max_cost, { currency })}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 4,
      label: "Avg Query Cost",
      uid: "avg_cost",
      render: item => <div>{formatNumber(item.avg_cost, { currency })}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 5,
      label: "Total Number of queries",
      uid: "num_queries",
      render: item => <div>{item.num_of_queries}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 6,
      label: "Number of Adhoc queries",
      uid: "adhoc_queries",
      render: item => <div>{item.num_of_adhoc_queries}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 7,
      label: "Number of Query Groups",
      uid: "num_query_groups",
      render: item => <div>{item.num_of_query_groups}</div>,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      id: 8,
      label: "Insights",
      uid: "insights",
      render: item => <ColorInsightTags tags={item.insights || []} />,
      width: "15%",
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        apiFunction: ({ prefix, page }) =>
          getGovernanceFilters({
            entity_type: GovernanceFilterEntityTypes.RolesInsights,
            prefix,
            page,
          }),
        pageSize: 50,
        seeMore: true,
        extractorFunction: data => {
          return (
            data?.roles?.insights?.map(t => ({
              label: t,
              value: t,
            })) || []
          );
        },
        searchKey: "tag",
        value: insightFilter,
        onChange: value => {
          updateSearchParamsByKey({ tag: value });
        },
      }),
    },
    {
      id: 9,
      label: "Instance",
      uid: "instance_id",
      width: "15%",
      render: item => (
        <div>{getInstanceName(item.instance_id, datastores ?? []) || item.instance_id}</div>
      ),
      filter: {
        filterType: FilterType.DROPDOWN,
        value: instanceFilter,
        searchKey: "instance",
        options: (datastores ?? [])
          .filter(instance => instance.instance_id != null)
          .map(instance => ({
            label: instance.name,
            value: instance.instance_id as string,
          })),
        onChange: value => {
          updateSearchParamsByKey({ instance: value });
        },
      },
    },
    {
      id: 10,
      label: "Default Role",
      uid: "default_role",
      width: "7%",
      render: item => <div className={styles.table_item_break_word}>{item.default_role}</div>,
    },
  ];

  if (isLoading) return <RelativeComponentLoader label={"Loading..."} />;

  if (isError || !roles) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load roles data. Please try again later.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent className="al-pt-4">
        <Stack direction="row" gap="sm" align="center" className="mb-4">
          <div className="text-black fs-4">Roles</div>
          <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.USERS} />
          <FilterTagWrapper
            applicableFor={FilterApplicableFor.GOVERNANCE_ROLES}
            filters={{
              roleFilter: {
                filterStr: roleFilter,
                label: "Role",
                searchKey: "role",
                onclose: (removedFilter?: string) => {
                  if (removedFilter) deleteSearchParamsByKeyValue({ role: removedFilter });
                },
              },
              insightFilter: {
                filterStr: insightFilter,
                label: "Tag",
                searchKey: "tag",
                onclose: (removedFilter?: string) => {
                  if (removedFilter) deleteSearchParamsByKeyValue({ tag: removedFilter });
                },
              },
              instanceFilter: {
                filterStr: instanceFilter
                  .map(instance => getInstanceName(instance, datastores ?? []))
                  .filter((x): x is string => x !== undefined),
                label: "Instance",
                searchKey: "instance",
                onclose: (removedFilter?: string) => {
                  const instanceId = (datastores ?? []).find(
                    ds => ds.name === removedFilter
                  )?.instance_id;
                  if (instanceId) {
                    deleteSearchParamsByKeyValue({ instance: instanceId });
                  }
                },
              },
            }}
          />
          <div className="spacer" />
          <div className={styles.duration}>Last 1 Month</div>
          <SavedFilters
            applicableFor={FilterApplicableFor.GOVERNANCE_ROLES}
            buttonProps={{ size: "sm" }}
          />
          <div id="columnpicker-container-roles" className={styles.columnpicker} />
          <DownloadTable
            handleDownload={handleDownloadClick}
            buttonProps={{
              size: "sm",
            }}
          />
        </Stack>
        <DataTable
          header={headers}
          data={roles}
          idKey="role_name"
          onRowClick={onItemClick}
          resetPage={() => setPage(0)}
          applicableFor={FilterApplicableFor.GOVERNANCE_ROLES}
          columnPickerPortal="columnpicker-container-roles"
          minWidth={1100}
          defaultSort={{ sortParamValue: "total_cost", order: "desc" }}
        />
        <Paginate
          itemCount={roles.total}
          page={page}
          pageSize={pageSize}
          numPages={roles.pages}
          onPageClick={setPage}
          onPageSizeChange={updatePageSize}
          showPageSize={true}
        />
      </CardContent>
    </Card>
  );
};

const Governance = () => {
  const location = useLocation();
  const isRolesTab = location.pathname.includes("roles");
  useBreadcrumb(isRolesTab ? "Roles" : "Users", {
    defaultParents: [{ label: "Users & Roles", pathname: "/governance", noLink: true }],
    truncateTo: "/governance",
  });
  const [selectedUser, setSelectedUser] = useState<GovernanceUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<GovernanceRole | null>(null);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);

  const defaultTab = isRolesTab ? 1 : 0;

  const { data: summary, isFetching: isSummaryLoading } = useQuery({
    queryKey: [GET_SUMMARY],
    queryFn: () => getGovernanceSummary(),
  });

  // Filter out admin permissions metric from summary
  const filteredSummary =
    summary
      ?.filter(item => !item.label.toLowerCase().includes("admin"))
      .map(item => ({ ...item, value: String(item.value) })) || [];

  const handleItemClick = (item: GovernanceUser | GovernanceRole) => {
    if ("user_name" in item) {
      setSelectedUser(item);
    } else {
      setSelectedRole(item);
    }
    setShowSidebar(true);
  };

  if (isSummaryLoading) return <RelativeComponentLoader label={"Loading..."} />;

  return (
    <>
      <Stack className="al-h-full" direction="column">
        <Stack className="mb-4" direction="row" align="center" justify="between">
          <Typography variant="page-heading">Users & Roles</Typography>
          <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.USERS} />
        </Stack>
        <div className="al-bg-blue-50 p-2 al-rounded-xs text-primary mb-3">
          This page shows insights around user costs and login activities
        </div>
        <FlexListSummary summary={filteredSummary} />
        <Tabs defaultValue={defaultTab === 1 ? "roles" : "users"} variant={"underlined"}>
          <TabsList>
            <TabsTrigger value="users" asChild>
              <Link to="/users">Users</Link>
            </TabsTrigger>
            <TabsTrigger value="roles" asChild>
              <Link to="/roles">Roles</Link>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="users">
            <UsersList onItemClick={handleItemClick} />
          </TabsContent>
          <TabsContent value="roles">
            <RolesList onItemClick={handleItemClick} />
          </TabsContent>
        </Tabs>
      </Stack>
      <Sheet
        open={showSidebar}
        onOpenChange={open => {
          if (!open) {
            setSelectedUser(null);
            setSelectedRole(null);
            setShowSidebar(false);
          }
        }}
      >
        <SheetContent
          side="right"
          className="!al-w-[692px] !al-max-w-none"
          aria-label="Governance details modal"
        >
          {selectedUser && <UserDetailsModal user_name={selectedUser.user_name} />}
          {selectedRole && <RoleDetailsModal role_name={selectedRole.role_name} />}
        </SheetContent>
      </Sheet>
    </>
  );
};

export { Governance };
